import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

export default function BusinessSelector() {
  const navigate = useNavigate();
  const { merchantData, setSelectedHierarchy } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  
  // Hierarchy state
  const [businesses, setBusinesses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [outlets, setOutlets] = useState([]);
  
  // Selected values
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);

  const steps = [
    { number: 1, title: 'Business', icon: 'bi-briefcase' },
    { number: 2, title: 'Country', icon: 'bi-globe' },
    { number: 3, title: 'State', icon: 'bi-map' },
    { number: 4, title: 'City', icon: 'bi-geo-alt' },
    { number: 5, title: 'Outlet', icon: 'bi-shop' }
  ];

  // Load businesses on mount
  useEffect(() => {
    if (merchantData?.id) {
      loadBusinesses();
    }
  }, [merchantData]);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      setError('');
      const businessesQuery = query(
        collection(db, 'businesses'),
        where('merchantId', '==', merchantData.id)
      );
      const snapshot = await getDocs(businessesQuery);
      const businessesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBusinesses(businessesData);
    } catch (err) {
      console.error('Error loading businesses:', err);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async (businessId) => {
    try {
      setLoading(true);
      setError('');
      const countriesQuery = query(
        collection(db, 'countries'),
        where('businessId', '==', businessId)
      );
      const snapshot = await getDocs(countriesQuery);
      const countriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCountries(countriesData);
    } catch (err) {
      console.error('Error loading countries:', err);
      setError('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const loadStates = async (countryId) => {
    try {
      setLoading(true);
      setError('');
      const statesQuery = query(
        collection(db, 'states'),
        where('countryId', '==', countryId)
      );
      const snapshot = await getDocs(statesQuery);
      const statesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStates(statesData);
    } catch (err) {
      console.error('Error loading states:', err);
      setError('Failed to load states');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async (stateId) => {
    try {
      setLoading(true);
      setError('');
      const citiesQuery = query(
        collection(db, 'cities'),
        where('stateId', '==', stateId)
      );
      const snapshot = await getDocs(citiesQuery);
      const citiesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCities(citiesData);
    } catch (err) {
      console.error('Error loading cities:', err);
      setError('Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  const loadOutlets = async (businessId, cityId) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading outlets for:', { businessId, cityId });
      
      // Try with both businessId and cityId first
      let outletsQuery = query(
        collection(db, 'outlets'),
        where('businessId', '==', businessId),
        where('cityId', '==', cityId)
      );
      let snapshot = await getDocs(outletsQuery);
      
      console.log('Outlets found with businessId+cityId:', snapshot.docs.length);
      
      // If no results, try with just cityId
      if (snapshot.docs.length === 0) {
        console.log('Trying query with just cityId...');
        outletsQuery = query(
          collection(db, 'outlets'),
          where('cityId', '==', cityId)
        );
        snapshot = await getDocs(outletsQuery);
        console.log('Outlets found with just cityId:', snapshot.docs.length);
      }
      
      // If still no results, try with just businessId
      if (snapshot.docs.length === 0) {
        console.log('Trying query with just businessId...');
        outletsQuery = query(
          collection(db, 'outlets'),
          where('businessId', '==', businessId)
        );
        snapshot = await getDocs(outletsQuery);
        console.log('Outlets found with just businessId:', snapshot.docs.length);
      }
      
      const outletsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Outlets data:', outletsData);
      setOutlets(outletsData);
    } catch (err) {
      console.error('Error loading outlets:', err);
      setError('Failed to load outlets');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSelect = (business) => {
    setSelectedBusiness(business);
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedOutlet(null);
    setCountries([]);
    setStates([]);
    setCities([]);
    setOutlets([]);
    loadCountries(business.id);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedOutlet(null);
    setStates([]);
    setCities([]);
    setOutlets([]);
    loadStates(country.id);
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setSelectedCity(null);
    setSelectedOutlet(null);
    setCities([]);
    setOutlets([]);
    loadCities(state.id);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setSelectedOutlet(null);
    setOutlets([]);
    loadOutlets(selectedBusiness.id, city.id);
  };

  const handleOutletSelect = (outlet) => {
    setSelectedOutlet(outlet);
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1: return selectedBusiness !== null;
      case 2: return selectedCountry !== null;
      case 3: return selectedState !== null;
      case 4: return selectedCity !== null;
      case 5: return selectedOutlet !== null;
      default: return false;
    }
  };

  const handleConfirmSelection = () => {
    if (!selectedOutlet) {
      setError('Please select an outlet to continue');
      return;
    }

    const hierarchy = {
      merchant: {
        id: merchantData.id,
        name: merchantData.name
      },
      business: {
        id: selectedBusiness.id,
        name: selectedBusiness.name
      },
      country: {
        id: selectedCountry.id,
        name: selectedCountry.name
      },
      state: {
        id: selectedState.id,
        name: selectedState.name
      },
      city: {
        id: selectedCity.id,
        name: selectedCity.name
      },
      outlet: {
        id: selectedOutlet.id,
        name: selectedOutlet.name
      }
    };

    // Save to context and localStorage
    setSelectedHierarchy(hierarchy);
    localStorage.setItem('selectedHierarchy', JSON.stringify(hierarchy));
    
    // Navigate to dashboard
    navigate('/');
  };

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{
      background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <div className="card shadow-lg border-0" style={{ borderRadius: '24px' }}>
              <div className="card-body p-4 p-md-5">
                <div className="text-center mb-4">
                  <i className="bi bi-building" style={{ fontSize: '3rem', color: '#2196F3' }}></i>
                  <h2 className="gradient-text fw-bold mt-3 mb-2">Business Hierarchy Setup</h2>
                  <p className="text-muted">Step {currentStep} of 5: Select your {steps[currentStep - 1].title}</p>
                </div>

                {/* Progress Steps */}
                <div className="mb-5">
                  <div className="d-flex justify-content-between position-relative">
                    {/* Progress Line */}
                    <div className="position-absolute w-100" style={{ top: '20px', left: 0, height: '2px', zIndex: 0 }}>
                      <div className="bg-light h-100"></div>
                      <div 
                        className="bg-primary h-100 position-absolute top-0 start-0 transition-all" 
                        style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
                      ></div>
                    </div>
                    
                    {/* Step Indicators */}
                    {steps.map((step) => (
                      <div key={step.number} className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 1 }}>
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center ${
                            step.number < currentStep 
                              ? 'bg-primary text-white' 
                              : step.number === currentStep 
                              ? 'bg-primary text-white border border-3 border-white' 
                              : 'bg-light text-muted'
                          }`}
                          style={{ 
                            width: '40px', 
                            height: '40px',
                            boxShadow: step.number === currentStep ? '0 0 0 4px rgba(33, 150, 243, 0.2)' : 'none'
                          }}
                        >
                          {step.number < currentStep ? (
                            <i className="bi bi-check-lg"></i>
                          ) : (
                            <i className={step.icon}></i>
                          )}
                        </div>
                        <small className={`mt-2 text-center ${step.number === currentStep ? 'fw-bold text-primary' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>
                          {step.title}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                  </div>
                )}

                {/* Step Content */}
                <div className="mb-4" style={{ minHeight: '300px' }}>
                  {/* Step 1: Business Selection */}
                  {currentStep === 1 && (
                    <div>
                      <h5 className="mb-3">
                        <i className="bi bi-briefcase me-2"></i>
                        Choose Your Business
                      </h5>
                      {loading && businesses.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="text-muted mt-3">Loading businesses...</p>
                        </div>
                      ) : businesses.length === 0 ? (
                        <div className="alert alert-warning">
                          <i className="bi bi-info-circle me-2"></i>
                          No businesses found for your merchant account.
                        </div>
                      ) : (
                        <div className="row g-3">
                          {businesses.map(business => (
                            <div key={business.id} className="col-md-6">
                              <div
                                className={`card hover-lift cursor-pointer h-100 ${
                                  selectedBusiness?.id === business.id ? 'border-primary border-2 shadow' : ''
                                }`}
                                onClick={() => handleBusinessSelect(business)}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              >
                                <div className="card-body">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3"
                                      style={{ width: '50px', height: '50px', minWidth: '50px' }}
                                    >
                                      <i className="bi bi-building-fill text-white fs-5"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{business.name}</h6>
                                      {business.description && (
                                        <small className="text-muted">{business.description}</small>
                                      )}
                                    </div>
                                    {selectedBusiness?.id === business.id && (
                                      <i className="bi bi-check-circle-fill text-primary fs-4"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Country Selection */}
                  {currentStep === 2 && (
                    <div>
                      <h5 className="mb-3">
                        <i className="bi bi-globe me-2"></i>
                        Choose Your Country
                      </h5>
                      {countries.length === 0 ? (
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          No countries available for the selected business.
                        </div>
                      ) : (
                        <div className="row g-3">
                          {countries.map(country => (
                            <div key={country.id} className="col-md-6">
                              <div
                                className={`card hover-lift cursor-pointer h-100 ${
                                  selectedCountry?.id === country.id ? 'border-primary border-2 shadow' : ''
                                }`}
                                onClick={() => handleCountrySelect(country)}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              >
                                <div className="card-body">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3"
                                      style={{ width: '50px', height: '50px', minWidth: '50px' }}
                                    >
                                      <i className="bi bi-flag-fill text-white fs-5"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{country.name}</h6>
                                      {country.code && (
                                        <small className="text-muted">{country.code}</small>
                                      )}
                                    </div>
                                    {selectedCountry?.id === country.id && (
                                      <i className="bi bi-check-circle-fill text-primary fs-4"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 3: State Selection */}
                  {currentStep === 3 && (
                    <div>
                      <h5 className="mb-3">
                        <i className="bi bi-map me-2"></i>
                        Choose Your State
                      </h5>
                      {states.length === 0 ? (
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          No states available for the selected country.
                        </div>
                      ) : (
                        <div className="row g-3">
                          {states.map(state => (
                            <div key={state.id} className="col-md-6">
                              <div
                                className={`card hover-lift cursor-pointer h-100 ${
                                  selectedState?.id === state.id ? 'border-primary border-2 shadow' : ''
                                }`}
                                onClick={() => handleStateSelect(state)}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              >
                                <div className="card-body">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3"
                                      style={{ width: '50px', height: '50px', minWidth: '50px' }}
                                    >
                                      <i className="bi bi-map-fill text-white fs-5"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{state.name}</h6>
                                      {state.code && (
                                        <small className="text-muted">{state.code}</small>
                                      )}
                                    </div>
                                    {selectedState?.id === state.id && (
                                      <i className="bi bi-check-circle-fill text-primary fs-4"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 4: City Selection */}
                  {currentStep === 4 && (
                    <div>
                      <h5 className="mb-3">
                        <i className="bi bi-geo-alt me-2"></i>
                        Choose Your City
                      </h5>
                      {cities.length === 0 ? (
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          No cities available for the selected state.
                        </div>
                      ) : (
                        <div className="row g-3">
                          {cities.map(city => (
                            <div key={city.id} className="col-md-6">
                              <div
                                className={`card hover-lift cursor-pointer h-100 ${
                                  selectedCity?.id === city.id ? 'border-primary border-2 shadow' : ''
                                }`}
                                onClick={() => handleCitySelect(city)}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              >
                                <div className="card-body">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3"
                                      style={{ width: '50px', height: '50px', minWidth: '50px' }}
                                    >
                                      <i className="bi bi-building text-white fs-5"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{city.name}</h6>
                                      {city.code && (
                                        <small className="text-muted">{city.code}</small>
                                      )}
                                    </div>
                                    {selectedCity?.id === city.id && (
                                      <i className="bi bi-check-circle-fill text-primary fs-4"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 5: Outlet Selection */}
                  {currentStep === 5 && (
                    <div>
                      <h5 className="mb-3">
                        <i className="bi bi-shop me-2"></i>
                        Choose Your Outlet
                      </h5>
                      {outlets.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="alert alert-warning">
                            <i className="bi bi-info-circle me-2"></i>
                            No outlets available for the selected city.
                          </div>
                          <p className="text-muted mb-3">
                            There are no outlets configured in <strong>{selectedCity?.name}</strong> yet.
                          </p>
                          <div className="d-flex gap-2 justify-content-center">
                            <button 
                              className="btn btn-outline-primary"
                              onClick={handleBack}
                            >
                              <i className="bi bi-arrow-left me-2"></i>
                              Choose Different City
                            </button>
                            <button 
                              className="btn btn-primary"
                              onClick={() => {
                                // TODO: Navigate to outlet creation page or contact admin
                                alert('Please contact your administrator to set up outlets in this city.');
                              }}
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              Request Outlet Setup
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="row g-3">
                          {outlets.map(outlet => (
                            <div key={outlet.id} className="col-md-6">
                              <div
                                className={`card hover-lift cursor-pointer h-100 ${
                                  selectedOutlet?.id === outlet.id ? 'border-primary border-2 shadow' : ''
                                }`}
                                onClick={() => handleOutletSelect(outlet)}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              >
                                <div className="card-body">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3"
                                      style={{ width: '50px', height: '50px', minWidth: '50px' }}
                                    >
                                      <i className="bi bi-shop-window text-white fs-5"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{outlet.name}</h6>
                                      {outlet.address && (
                                        <small className="text-muted">{outlet.address}</small>
                                      )}
                                    </div>
                                    {selectedOutlet?.id === outlet.id && (
                                      <i className="bi bi-check-circle-fill text-primary fs-4"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex gap-2 justify-content-between align-items-center pt-3 border-top">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => currentStep === 1 ? navigate('/login') : handleBack()}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    {currentStep === 1 ? 'Back to Login' : 'Previous'}
                  </button>
                  
                  {selectedBusiness && (
                    <div className="text-center small text-muted">
                      <i className="bi bi-building me-1"></i>
                      {selectedBusiness.name}
                      {selectedCountry && ` → ${selectedCountry.name}`}
                      {selectedState && ` → ${selectedState.name}`}
                      {selectedCity && ` → ${selectedCity.name}`}
                      {selectedOutlet && ` → ${selectedOutlet.name}`}
                    </div>
                  )}

                  {currentStep < 5 ? (
                    <button
                      className="btn btn-primary"
                      onClick={handleNext}
                      disabled={!canProceedToNext() || loading}
                    >
                      Next
                      <i className="bi bi-arrow-right ms-2"></i>
                    </button>
                  ) : (
                    <button
                      className="btn btn-success"
                      onClick={handleConfirmSelection}
                      disabled={!selectedOutlet || loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Loading...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Continue to Dashboard
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
